import React, { useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Chip, Container, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { getAdminOfferUrl } from '~/components/Marketing/OfferBlocklist/utils';
import { TAG_TYPE_OPTIONS, TagTypeValue } from '~/components/Tags/constants';
import { mapExperienceOfferItem } from '~/components/Tags/hooks/useExperienceOffersByPlaceIds';

import { useGetExperienceOffersByIds } from '~/hooks/apis/experiences/useGetExperienceOffersByIds';
import { useGetPublicOffersByIds } from '~/hooks/apis/public-offer/useGetPublicOffersByIds';
import { useGetTagAssociations } from '~/hooks/apis/tags/useGetTagAssociations';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';
import useDebounced from '~/hooks/useDebounced';

import { readPublicOfferFields } from '~/services/PublicOfferService';
import type { GetTagAssociationsParams } from '~/services/TagsService';

import TagAssociationOfferCell from './TagAssociationOfferCell';
import { getExperienceOnlineStatus, getHotelOnlineStatus } from './tagAssociationOfferStatus';
import { TagAssociationRow, mapTagAssociationRows } from './tagAssociationRows';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const ASSOCIATION_ENTITY_TYPES: GetTagAssociationsParams['entityTypes'] = ['HOTEL', 'EXPERIENCE'];

interface RowMenu {
  row: TagAssociationRow;
  position: { top: number; left: number };
}

export default function TagAssociationsPage() {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [name, setName] = useState('');
  const [tagType, setTagType] = useState<TagTypeValue | ''>('');
  const [rowMenu, setRowMenu] = useState<RowMenu | null>(null);
  const debouncedName = useDebounced(name.trim(), 300);
  const { paginationModel, setPaginationModel } = useDataGridPagination({ page: 0, pageSize: 25 });
  const [previousName, setPreviousName] = useState(debouncedName);

  if (previousName !== debouncedName) {
    setPreviousName(debouncedName);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }

  const associations = useGetTagAssociations({
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    name: debouncedName || undefined,
    tagType: tagType || undefined,
    entityTypes: ASSOCIATION_ENTITY_TYPES,
  });
  const rows = useMemo(() => mapTagAssociationRows(associations.data?.result ?? []), [associations.data?.result]);
  const hotels = useGetPublicOffersByIds(
    rows.flatMap((row) => (row.hotelId ? [row.hotelId] : [])),
    'AU',
    brand,
  );
  const experiences = useGetExperienceOffersByIds(rows.flatMap((row) => (row.experienceId ? [row.experienceId] : [])));
  const hotelUrl = rowMenu?.row.hotelId ? getAdminOfferUrl(rowMenu.row.hotelId) : '';
  const experienceUrl = rowMenu?.row.experienceId ? `/experience/${encodeURIComponent(rowMenu.row.experienceId)}` : '';

  const openRowMenu = (row: TagAssociationRow, position: RowMenu['position']) => {
    if (row.hotelId || row.experienceId) {
      setRowMenu({ row, position });
    }
  };

  const columns: Array<GridColDef<TagAssociationRow>> = [
    {
      field: 'tagName',
      headerName: 'Tag Name',
      flex: 1,
      minWidth: 180,
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => <Chip label={row.tagName} size="small" title={row.tagName} />,
    },
    {
      field: 'tagType',
      headerName: 'Tag Type',
      width: 160,
      sortable: false,
      valueFormatter: (value) => TAG_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value,
    },
    {
      field: 'hotelId',
      headerName: 'Hotel',
      flex: 2,
      minWidth: 280,
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const hotel = hotels.offersById.get(row.hotelId);
        const fields = readPublicOfferFields(hotel);
        return (
          <TagAssociationOfferCell
            id={row.hotelId}
            title={fields.hotelName || fields.offerName}
            imageId={hotel && 'images' in hotel ? hotel.images?.[0]?.id : undefined}
            isOnline={getHotelOnlineStatus(hotel)}
            isLoading={hotels.isLoading || associations.isPlaceholderData}
          />
        );
      },
    },
    {
      field: 'experienceId',
      headerName: 'Experience',
      flex: 2,
      minWidth: 280,
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const experience = experiences.data?.get(row.experienceId);
        const item = experience ? mapExperienceOfferItem(experience) : undefined;
        return (
          <TagAssociationOfferCell
            id={row.experienceId}
            title={item?.title}
            imageId={item?.imageId}
            imageUrl={item?.imageUrl}
            isOnline={getExperienceOnlineStatus(experience)}
            isLoading={experiences.isLoading || associations.isPlaceholderData}
          />
        );
      },
    },
  ];

  return (
    <Container maxWidth={false} sx={{ pt: 3 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Search by tag name"
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Tag type"
            size="small"
            value={tagType}
            onChange={(event) => {
              setTagType(event.target.value as TagTypeValue | '');
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All tag types</MenuItem>
            {TAG_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {associations.isError && <Alert severity="error">{associations.error.message}</Alert>}
        {(hotels.isError || experiences.isError) && (
          <Alert severity="warning">Some offer details could not be loaded. Their IDs are still shown.</Alert>
        )}
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          getRowId={(row) => JSON.stringify([row.tagId, row.hotelId, row.experienceId])}
          onRowClick={({ row }, event) => openRowMenu(row, { top: event.clientY, left: event.clientX })}
          onCellKeyDown={({ row }, event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.defaultMuiPrevented = true;
              const bounds = event.currentTarget.getBoundingClientRect();
              openRowMenu(row, { top: bounds.bottom, left: bounds.left });
            }
          }}
          loading={associations.isFetching}
          rowHeight={80}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rowCount={associations.data?.pagination.total_items ?? 0}
          slots={{ pagination: GridPagination }}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnFilter
          localeText={{ noRowsLabel: 'No tag associations found' }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Stack>
      <Menu
        open={!!rowMenu && !!(hotelUrl || experienceUrl)}
        onClose={() => setRowMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={rowMenu?.position}
        slotProps={{ list: { 'aria-label': 'Offer links' } }}
      >
        {!!hotelUrl && (
          <MenuItem
            component={RouterLink}
            to={hotelUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open hotel in a new tab"
            onClick={() => setRowMenu(null)}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open hotel</ListItemText>
          </MenuItem>
        )}
        {!!experienceUrl && (
          <MenuItem
            component={RouterLink}
            to={experienceUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open experience in a new tab"
            onClick={() => setRowMenu(null)}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open experience</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Container>
  );
}
